import { useState, useEffect, useCallback } from 'react';
import { api } from '@shared/services/api';

interface OnboardingState {
  shouldShowOnboarding: boolean;
  isLoading: boolean;
  hasAgents: boolean;
  hasBackupConfigs: boolean;
  skipOnboarding: () => void;
  completeOnboarding: () => void;
  refetch: () => void;
}

const ONBOARDING_SKIPPED_KEY = 'onboarding_skipped';

export function useOnboarding(): OnboardingState {
  const [isLoading, setIsLoading] = useState(true);
  const [hasAgents, setHasAgents] = useState(false);
  const [hasBackupConfigs, setHasBackupConfigs] = useState(false);
  const [isSkipped, setIsSkipped] = useState(false);

  const checkOnboardingStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      // Check if onboarding was skipped
      const skipped = localStorage.getItem(ONBOARDING_SKIPPED_KEY) === 'true';
      setIsSkipped(skipped);

      // Fetch agents
      const agentsResponse = await api.get('/agents');
      const agents = agentsResponse.data || [];
      setHasAgents(agents.length > 0);

      // Fetch backup configs
      const configsResponse = await api.get('/backups/configs');
      const configs = configsResponse.data || [];
      setHasBackupConfigs(configs.length > 0);
    } catch (error) {
      console.error('Failed to check onboarding status:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  const skipOnboarding = useCallback(() => {
    localStorage.setItem(ONBOARDING_SKIPPED_KEY, 'true');
    setIsSkipped(true);
  }, []);

  const completeOnboarding = useCallback(() => {
    localStorage.setItem(ONBOARDING_SKIPPED_KEY, 'true');
    setIsSkipped(true);
  }, []);

  const refetch = useCallback(() => {
    checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  // Show onboarding if:
  // - Not loading
  // - Not skipped
  // - No agents OR (has agents but no backup configs)
  const shouldShowOnboarding = !isLoading && !isSkipped && (!hasAgents || !hasBackupConfigs);

  return {
    shouldShowOnboarding,
    isLoading,
    hasAgents,
    hasBackupConfigs,
    skipOnboarding,
    completeOnboarding,
    refetch,
  };
}
